import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import AdminPageShell from '../admin/AdminPageShell';
import ApprenantPageShell from '../apprenant/ApprenantPageShell';
import FormateurPageShell from '../formateur/FormateurPageShell';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { usePublicBranding } from '../../hooks/usePublicBranding';
import {
  getPortalHelpConfig,
  type HelpSection,
  type PortalHelpRole,
} from '../../config/portalHelpContent';
import { downloadPortalHelpPdf } from '../../services/portalHelpService';

type Props = {
  portal: PortalHelpRole;
};

function normalizeSearch(value: string) {
  return value.trim().toLowerCase();
}

function sectionMatchesQuery(section: HelpSection, query: string) {
  if (!query) return true;
  const haystack = [
    section.title,
    section.summary,
    ...section.steps,
    ...(section.tips ?? []),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

function HelpSectionCard({ section }: { section: HelpSection }) {
  return (
    <details className="group rounded-2xl border border-outline-variant/15 bg-white shadow-sm open:shadow-md">
      <summary className="flex cursor-pointer list-none items-start gap-3 px-5 py-4 [&::-webkit-details-marker]:hidden">
        <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <LucideSymbolIcon name={section.icon} tone="inherit" size={20} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center justify-between gap-2">
            <span className="text-base font-bold text-slate-900">{section.title}</span>
            <LucideSymbolIcon
              name="expand_more"
              tone="muted"
              size={20}
              className="shrink-0 transition-transform group-open:rotate-180"
            />
          </span>
          <p className="mt-1 text-sm leading-relaxed text-on-surface-variant">{section.summary}</p>
        </span>
      </summary>
      <div className="border-t border-outline-variant/10 px-5 pb-5 pt-4">
        <ol className="space-y-2 text-sm leading-relaxed text-on-surface-variant">
          {section.steps.map((step) => (
            <li key={step} className="flex gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
              <span>{step}</span>
            </li>
          ))}
        </ol>
        {section.tips?.length ? (
          <ul className="mt-4 space-y-2 rounded-xl bg-amber-50/80 px-4 py-3 text-sm text-amber-950">
            {section.tips.map((tip) => (
              <li key={tip} className="flex gap-2">
                <LucideSymbolIcon name="info" tone="inherit" size={16} className="mt-0.5 shrink-0 text-amber-700" />
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {section.linkTo ? (
          <Link
            to={section.linkTo}
            className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-primary hover:underline"
          >
            {section.linkLabel ?? 'Ouvrir le module'}
            <LucideSymbolIcon name="arrow_forward" tone="inherit" size={16} />
          </Link>
        ) : null}
      </div>
    </details>
  );
}

export default function PortalHelpPanel({ portal }: Props) {
  const config = getPortalHelpConfig(portal);
  const branding = usePublicBranding();
  const [query, setQuery] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const normalizedQuery = normalizeSearch(query);

  const filteredSections = useMemo(
    () => config.sections.filter((section) => sectionMatchesQuery(section, normalizedQuery)),
    [config.sections, normalizedQuery],
  );

  const filteredFaqs = useMemo(() => {
    if (!normalizedQuery) return config.faqs;
    return config.faqs.filter((faq) =>
      `${faq.question} ${faq.answer}`.toLowerCase().includes(normalizedQuery),
    );
  }, [config.faqs, normalizedQuery]);

  const Shell =
    portal === 'apprenant'
      ? ApprenantPageShell
      : portal === 'formateur'
        ? FormateurPageShell
        : AdminPageShell;

  const supportMailto = `mailto:${branding.supportEmail}?subject=${encodeURIComponent(
    `Assistance ${config.roleLabel} — JCONSULT MY Academia`,
  )}`;
  const supportPhoneHref = branding.supportPhone
    ? `tel:${branding.supportPhone.replace(/\s/g, '')}`
    : null;

  const handleDownloadPdf = async () => {
    setPdfError('');
    setPdfLoading(true);
    try {
      await downloadPortalHelpPdf(portal);
    } catch (error) {
      setPdfError(error instanceof Error ? error.message : 'Impossible de générer le PDF.');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <Shell
      title={config.pageTitle}
      description={config.pageDescription}
      sideCard={{
        label: 'Modules documentés',
        value: String(config.sections.length),
        helper: `${config.faqs.length} question(s) fréquente(s)`,
      }}
    >
      <div className="space-y-6">
        <div className="rounded-2xl border border-outline-variant/15 bg-white p-5 shadow-sm">
          <p className="text-sm leading-relaxed text-on-surface-variant">{config.intro}</p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={() => void handleDownloadPdf()}
              disabled={pdfLoading}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-bold text-white shadow-md transition hover:bg-primary-dark disabled:cursor-wait disabled:opacity-70"
            >
              <LucideSymbolIcon name="download" tone="inherit" size={18} className="text-white" />
              {pdfLoading ? 'Génération du PDF…' : 'Télécharger le guide PDF (hors ligne)'}
            </button>
            <p className="text-xs leading-relaxed text-on-surface-variant sm:max-w-md sm:text-right">
              Notice complète avec schémas des écrans, étapes détaillées et FAQ — lisible sans connexion.
            </p>
          </div>
          {pdfError ? (
            <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{pdfError}</p>
          ) : null}
          <div className="relative mt-4">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2">
              <LucideSymbolIcon name="search" tone="muted" size={18} />
            </span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Rechercher un module, une étape, une question…"
              className="w-full rounded-xl border border-outline-variant/20 bg-surface-container-lowest py-3 pl-11 pr-4 text-sm placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>

        <section className="rounded-2xl border border-primary/15 bg-primary/[0.04] p-5">
          <h2 className="text-lg font-black text-slate-900">{config.gettingStarted.title}</h2>
          <ol className="mt-3 space-y-2">
            {config.gettingStarted.steps.map((step, index) => (
              <li key={step} className="flex gap-3 text-sm leading-relaxed text-on-surface-variant">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
                  {index + 1}
                </span>
                <span className="pt-0.5">{step}</span>
              </li>
            ))}
          </ol>
        </section>

        <section>
          <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
            <h2 className="text-lg font-black text-slate-900">Modules & fonctionnalités</h2>
            <p className="text-xs text-on-surface-variant">
              {filteredSections.length} section(s) affichée(s)
            </p>
          </div>
          {filteredSections.length > 0 ? (
            <div className="space-y-3">
              {filteredSections.map((section) => (
                <HelpSectionCard key={section.id} section={section} />
              ))}
            </div>
          ) : (
            <p className="rounded-xl border border-dashed border-outline-variant/25 bg-slate-50 px-4 py-8 text-center text-sm text-on-surface-variant">
              Aucun résultat pour « {query} ». Essayez un autre mot-clé ou contactez le support.
            </p>
          )}
        </section>

        {filteredFaqs.length > 0 ? (
          <section>
            <h2 className="mb-4 text-lg font-black text-slate-900">Questions fréquentes</h2>
            <div className="space-y-3">
              {filteredFaqs.map((faq) => (
                <details
                  key={faq.question}
                  className="rounded-2xl border border-outline-variant/15 bg-white px-5 py-4 shadow-sm"
                >
                  <summary className="cursor-pointer list-none text-sm font-bold text-slate-900 [&::-webkit-details-marker]:hidden">
                    {faq.question}
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-on-surface-variant">{faq.answer}</p>
                </details>
              ))}
            </div>
          </section>
        ) : null}

        <section className="rounded-2xl border border-outline-variant/15 bg-slate-900 px-6 py-6 text-white">
          <h2 className="text-lg font-black">Besoin d’un accompagnement humain ?</h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/75">
            Si vous ne trouvez pas la réponse ici, l’équipe support du centre peut vous aider. Précisez
            votre rôle ({config.roleLabel}), le module concerné et, si possible, une capture d’écran ou
            une référence de transaction.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href={supportMailto}
              className="inline-flex items-center justify-center rounded-xl bg-white px-5 py-2.5 text-sm font-bold text-primary shadow-md transition hover:scale-[1.02]"
            >
              Écrire au support
            </a>
            {supportPhoneHref ? (
              <a
                href={supportPhoneHref}
                className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10"
              >
                <LucideSymbolIcon name="call" tone="inherit" size={18} />
                {branding.supportPhone}
              </a>
            ) : null}
          </div>
        </section>
      </div>
    </Shell>
  );
}
